import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Plus, Edit, Trash2, User, Mail, Phone, Building2, DollarSign, FileText, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { getSocios, createSocio, updateSocio, deleteSocio } from '../services/apiService';

const ListadoSocios = () => {
  const [socios, setSocios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [socioEditando, setSocioEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    razonSocial: '',
    nifCif: '',
    telefono: '',
    email: '',
    inversion: ''
  });

  // Cargar socios desde la API
  useEffect(() => {
    cargarSocios();
  }, []);

  const cargarSocios = async () => {
    try {
      setCargando(true);
      const datos = await getSocios();
      setSocios(datos || []);
    } catch (error) {
      console.error('Error cargando socios:', error);
      alert('Error al cargar los socios. Por favor, recarga la página.');
    } finally {
      setCargando(false);
    }
  };

  const handleNuevoSocio = () => {
    setSocioEditando(null);
    setFormData({
      nombre: '',
      apellidos: '',
      razonSocial: '',
      nifCif: '',
      telefono: '',
      email: '',
      inversion: ''
    });
    setMostrarFormulario(true);
  };

  const handleEditar = (socio) => {
    setSocioEditando(socio);
    setFormData({
      nombre: socio.nombre || '',
      apellidos: socio.apellidos || '',
      razonSocial: socio.razon_social || '',
      nifCif: socio.nif_cif || '',
      telefono: socio.telefono || '',
      email: socio.email || '',
      inversion: socio.inversion || ''
    });
    setMostrarFormulario(true);
  };

  const handleEliminar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este socio?')) {
      try {
        await deleteSocio(id);
        await cargarSocios(); // Recargar lista
      } catch (error) {
        console.error('Error eliminando socio:', error);
        alert('Error al eliminar el socio. Por favor, intenta de nuevo.');
      }
    }
  };

  const handleGuardar = async () => {
    if (!formData.nombre || !formData.apellidos || !formData.email) {
      alert('Por favor completa los campos obligatorios: Nombre, Apellidos y Email');
      return;
    }

    try {
      const socioData = {
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        razon_social: formData.razonSocial || null,
        nif_cif: formData.nifCif || null,
        telefono: formData.telefono || null,
        email: formData.email,
        inversion: formData.inversion ? parseFloat(formData.inversion.replace(/[€,\s]/g, '')) || null : null
      };

      if (socioEditando) {
        // Editar existente
        await updateSocio(socioEditando.id, socioData);
      } else {
        // Crear nuevo
        await createSocio(socioData);
      }
      
      await cargarSocios(); // Recargar lista
      setCurrentPage(1); // Volver a la primera página
      setMostrarFormulario(false);
      setSocioEditando(null);
      setFormData({
        nombre: '',
        apellidos: '',
        razonSocial: '',
        nifCif: '',
        telefono: '',
        email: '',
        inversion: ''
      });
    } catch (error) {
      console.error('Error guardando socio:', error);
      alert('Error al guardar el socio. Por favor, intenta de nuevo.');
    }
  };

  const handleCancelar = () => {
    setMostrarFormulario(false);
    setSocioEditando(null);
    setFormData({
      nombre: '',
      apellidos: '',
      razonSocial: '',
      nifCif: '',
      telefono: '',
      email: '',
      inversion: ''
    });
  };

  if (mostrarFormulario) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {socioEditando ? 'Editar Socio' : 'Nuevo Socio'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {socioEditando 
                ? 'Modifica la información del socio'
                : 'Agrega un nuevo socio al club de inversión'}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Información del Socio</CardTitle>
            <CardDescription>
              Datos personales y de contacto del socio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: Juan"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Apellidos <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.apellidos}
                  onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: Pérez García"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">
                  Razón Social
                </label>
                <input
                  type="text"
                  value={formData.razonSocial}
                  onChange={(e) => setFormData({ ...formData, razonSocial: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: Empresa SL"
                />
                <p className="text-xs text-muted-foreground">Opcional</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  NIF/CIF
                </label>
                <input
                  type="text"
                  value={formData.nifCif}
                  onChange={(e) => setFormData({ ...formData, nifCif: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: 12345678A"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: +34 600 000 000"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="socio@email.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Inversión
                </label>
                <input
                  type="text"
                  value={formData.inversion}
                  onChange={(e) => setFormData({ ...formData, inversion: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: €50,000"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/50">
              <Button variant="outline" onClick={handleCancelar}>
                Cancelar
              </Button>
              <Button onClick={handleGuardar}>
                <Save className="h-4 w-4 mr-2" />
                {socioEditando ? 'Guardar Cambios' : 'Crear Socio'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Listado de Socios</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona los socios del club de inversión
          </p>
        </div>
        <Button onClick={handleNuevoSocio}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Socio
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Socios ({socios.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {cargando ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-muted-foreground">Cargando socios...</p>
            </div>
          ) : socios.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <User className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No hay socios registrados</p>
              <Button onClick={handleNuevoSocio}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primer Socio
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {socios
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((socio) => (
                <div
                  key={socio.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/30 transition-colors gap-4"
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-x-4 gap-y-2 items-center min-w-0">
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {socio.nombre} {socio.apellidos}
                        </p>
                        {socio.razon_social && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                            <Building2 className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{socio.razon_social}</span>
                          </p>
                        )}
                      </div>
                      <div className="text-sm min-w-0">
                        {socio.nif_cif ? (
                          <p className="text-muted-foreground flex items-center gap-1">
                            <FileText className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{socio.nif_cif}</span>
                          </p>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="text-sm min-w-0">
                        {socio.telefono ? (
                          <p className="text-muted-foreground flex items-center gap-1">
                            <Phone className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{socio.telefono}</span>
                          </p>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                      <div className="text-sm min-w-0">
                        <p className="text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{socio.email}</span>
                        </p>
                      </div>
                      <div className="text-sm min-w-0">
                        {socio.inversion ? (
                          <p className="text-muted-foreground flex items-center gap-1">
                            <DollarSign className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">€{parseFloat(socio.inversion).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </p>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditar(socio)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEliminar(socio.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                  ))}
              </div>
              
              {/* Paginación */}
              {socios.length > itemsPerPage && (
                <div className="flex items-center justify-between pt-4 border-t border-border/50 mt-4">
                  <div className="text-sm text-muted-foreground">
                    Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, socios.length)} de {socios.length} socios
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Anterior
                    </Button>
                    <div className="text-sm">
                      Página {currentPage} de {Math.ceil(socios.length / itemsPerPage)}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(Math.ceil(socios.length / itemsPerPage), prev + 1))}
                      disabled={currentPage >= Math.ceil(socios.length / itemsPerPage)}
                    >
                      Siguiente
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ListadoSocios;

